import { createRootRouteWithContext, redirect } from '@tanstack/react-router';
import { authnClient } from '@frontend/auth';
import {
  AUTH_CHECK_IGNORE_ROUTES,
  getJTI,
  getLoginData,
  getUser,
  getWeaveToken,
  hasLocationAccess,
  isWeaveTokenBufferActive,
  type PortalUser,
} from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';

type Context = {
  authenticated: boolean;
  user: PortalUser | undefined;
  lastLocationId: string | undefined;
};

// Public routes must stay reachable for a user with no locations, otherwise the redirect below would
// bounce off /sign-in-error (and the sign out flow) forever. Matches how auth-check.ts skips them.
const isPublicPath = (pathname: string) => AUTH_CHECK_IGNORE_ROUTES.some((route) => pathname.includes(route));

export const Route = createRootRouteWithContext<Context>()({
  beforeLoad: async ({ location }) => {
    const token = getWeaveToken();
    const jti = getJTI();
    http.setAuthorizationHeader(token, jti);

    const authenticated = authnClient.isUserAuthenticated() || isWeaveTokenBufferActive();

    // There is nothing an authenticated user with no location access can do in the app, so send them to the
    // error page. This has to happen here rather than in a component because: by render time the router has already
    // committed to the private route, and navigating out of render re-enters this same render loop. Also the
    // desktop app never hits the sign-in callback route because the token exchange happens in the electron main process.
    if (authenticated && !hasLocationAccess() && !isPublicPath(location.pathname)) {
      throw redirect({ to: '/sign-in-error', search: { error: 'no-location-access' }, replace: true });
    }

    const storedUserMatchesTokenUser = authnClient.storedUserIsLoggedInUser();
    const storedUser = getUser();

    const user: PortalUser | undefined =
      token && !!storedUser && storedUserMatchesTokenUser ? storedUser : await fetchUser();
    const lastLocationId = user ? getLoginData(user.userID)?.latestLocationIds?.[0] : undefined;
    /**
     * Data returned here will be available on each route, accessible via `useMatch`
     * We could test for page access here as well (authenticated but not authorized)
     */
    return { authenticated, user, lastLocationId };
  },
});

/**
 * Fetch user data but ignore failures because no token found. This is called on all routes, even public routes.
 * Public routes have no concept of a logged in user, so this may fail. This makes it fail gracefully.
 */
async function fetchUser(): Promise<PortalUser | undefined> {
  try {
    return await authnClient.fetchUser();
  } catch (error) {
    console.warn('Error fetching user in __root route. Ignore this if public route.', error);
    return undefined;
  }
}
